import React, { useState, useCallback, useEffect, useRef } from 'react';
import { z } from 'zod';
import { useTranslation } from '../../../../shared/i18n';
import { createProgressQuery, pollUntilComplete } from '../../api/activityProgress';
import { completeSession, saveProgress, markServiceOpened } from '../../api/activitySessions';
import { CredentialsModal } from './CredentialsModal';
import { OnboardingModal } from './OnboardingModal';
import { TimeBasedProgressBar } from './TimeBasedProgressBar';

// Number of completed sessions before skipping onboarding
const ONBOARDING_THRESHOLD = 3;

/**
 * Schema for valid snapshot format: { course_id => number | null }
 * Old format had objects: { course_id => { course_id, score, max_score } }
 */
const SnapshotSchema = z.record(
  z.string(),
  z.union([z.number(), z.null()])
).nullable().optional();

/**
 * Validates that a snapshot matches the expected simplified format
 */
const isValidSnapshotFormat = (snapshot) => {
  const result = SnapshotSchema.safeParse(snapshot);
  return result.success;
};

/**
 * ExternalServiceSession - Handles activity sessions for external services
 * (Duolingo, Khan Academy, CodeCombat)
 * - Shows loading panel while fetching initial snapshot
 * - Displays current progress from initial snapshot
 * - Handles completion checking with async progress polling
 * - Shows no-progress modal when progress is insufficient
 */
export const ExternalServiceSession = ({
  session,
  activity,
  onComplete,
  onSkipActivity,
  onShowNoProgressModal
}) => {
  const { t } = useTranslation();
  const [currentProgress, setCurrentProgress] = useState(session.initial_snapshot || {});
  const [isLoadingSnapshot, setIsLoadingSnapshot] = useState(
    activity.requires_initial_snapshot && Object.keys(session.initial_snapshot || {}).length === 0
  );
  const [isCheckingCompletion, setIsCheckingCompletion] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [loadingError, setLoadingError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [serviceOpenedAt, setServiceOpenedAt] = useState(session.service_opened_at);
  // Detect invalid (stale) snapshot format on mount
  const [hasInvalidSnapshot, setHasInvalidSnapshot] = useState(
    !isValidSnapshotFormat(session.initial_snapshot)
  );
  // Set when the API signals the guardian must reconnect the OAuth account.
  // Overrides the generic error UI so the kid gets specific guidance while
  // still being able to retry or skip.
  const [needsAccountReauth, setNeedsAccountReauth] = useState(false);

  const isAccountReauthError = (error) =>
    error?.response?.status === 422 &&
    error?.response?.data?.code === 'account_reauthentication_required';

  // Check if onboarding is needed (first 3 uses of this activity)
  const needsOnboarding = (activity.completed_sessions_count ?? 0) < ONBOARDING_THRESHOLD;

  // Load initial snapshot for external services
  const fetchInitialSnapshot = useCallback(async () => {
    setIsLoadingSnapshot(true);
    setLoadingError(null);
    setNeedsAccountReauth(false);

    try {
      let queryId;
      try {
        const response = await createProgressQuery(activity.id, session.id);
        queryId = response.query_id;
      } catch (createError) {
        if (isAccountReauthError(createError)) {
          setNeedsAccountReauth(true);
          return;
        }
        throw createError;
      }
      const outputs = await pollUntilComplete(queryId);

      // New simplified format: outputs is { course_id => score } directly
      // Check for error first
      if (outputs.error) {
        throw new Error(outputs.error);
      }

      // The outputs is now directly the snapshot: { course_id => score }
      // Filter out metadata fields and use as snapshot
      const snapshot = { ...outputs };
      delete snapshot.partial;

      // If empty (fresh account), populate with assigned courses at 0
      const assignedCourses = activity.external_config?.courses || [];
      if (Object.keys(snapshot).length === 0 && assignedCourses.length > 0) {
        assignedCourses.forEach(courseId => {
          snapshot[courseId] = 0;
        });
      }

      setCurrentProgress(snapshot);
      // Save the initial snapshot to the session
      await saveProgress(session.id, snapshot);
    } catch (error) {
      console.error('Failed to fetch initial snapshot:', error);
      setLoadingError(error.message || 'Failed to connect to service');
    } finally {
      setIsLoadingSnapshot(false);
      setIsRetrying(false);
    }
  }, [activity.id, session.id]);

  useEffect(() => {
    if (!activity.requires_initial_snapshot || Object.keys(currentProgress).length > 0) {
      setIsLoadingSnapshot(false);
      return;
    }

    fetchInitialSnapshot();
  }, [activity.requires_initial_snapshot, currentProgress, fetchInitialSnapshot]);

  // Handle retry for initial snapshot loading
  const handleRetryLoading = useCallback(() => {
    setIsRetrying(true);
    fetchInitialSnapshot();
  }, [fetchInitialSnapshot]);

  // Handle opening the external service (e.g., clicking "Open Duolingo")
  const handleOpenService = useCallback(async () => {
    setShowCredentialsModal(true);

    // Mark service as opened if not already
    if (!serviceOpenedAt) {
      try {
        const result = await markServiceOpened(session.id);
        setServiceOpenedAt(result.service_opened_at);
      } catch (error) {
        console.error('Failed to mark service as opened:', error);
        // Still show modal even if API call fails
      }
    }
  }, [session.id, serviceOpenedAt]);

  // Handle "Open Service" button click - show onboarding for new users
  const handleOpenServiceClick = useCallback(() => {
    if (needsOnboarding) {
      setShowOnboardingModal(true);
    } else {
      handleOpenService();
    }
  }, [needsOnboarding, handleOpenService]);

  // Handle onboarding completion - proceed to open service
  const handleOnboardingComplete = useCallback(() => {
    handleOpenService();
  }, [handleOpenService]);

  // Handle completion check for external services
  const handleCheckCompletion = useCallback(async () => {
    setIsCheckingCompletion(true);
    setNeedsAccountReauth(false);

    try {
      let queryId;
      try {
        const response = await createProgressQuery(activity.id, session.id);
        queryId = response.query_id;
      } catch (createError) {
        if (isAccountReauthError(createError)) {
          setNeedsAccountReauth(true);
          return;
        }
        throw createError;
      }
      const outputs = await pollUntilComplete(queryId);

      // Check for error first
      if (outputs.error) {
        throw new Error(outputs.error);
      }

      // New simplified format: outputs is { course_id => score } directly
      const finalSnapshot = { ...outputs };
      delete finalSnapshot.partial;

      // Complete the session
      const result = await completeSession(session.id, finalSnapshot);

      if (result.status === 'already_completed' && result.redirect_to) {
        // Session was already completed out-of-band (race condition / stale UI).
        // Gracefully redirect to the dashboard so the kid can move on to the next
        // activity instead of being told to skip.
        window.location.href = result.redirect_to;
        return;
      }
      if (result.error) {
        // API returned an error (e.g., session cancelled)
        if (result.error.code === 'SESSION_CANCELLED' && result.redirect_to) {
          // Session was cancelled by guardian (courses/username changed), redirect to dashboard
          window.location.href = result.redirect_to;
          return;
        }
        setErrorMessage(result.error.message);
      } else if (result.status === 'success') {
        // Success - notify parent to refresh dashboard
        onComplete(result);
      } else if (result.status === 'skipped') {
        // Skip request was approved (fallback when ActionCable is blocked)
        // Store message in sessionStorage for flash display and redirect
        sessionStorage.setItem('flash_notice', result.message);
        window.location.href = '/children';
      } else if (result.status === 'insufficient_progress') {
        // Show no progress modal
        onShowNoProgressModal(result.progress_comparison);
      }
    } catch (error) {
      console.error('Error checking completion:', error);
      setErrorMessage(error.message || t('children.activity_sessions.no_progress_dialog.error'));
    } finally {
      setIsCheckingCompletion(false);
    }
  }, [activity.id, session.id, onComplete, onShowNoProgressModal, t]);

  // Get translated texts
  const journeyTitle = t(activity.journey_title_key);
  const instructionData = activity.instruction_text_data;
  const instructionText = t(instructionData.key, instructionData);
  const completedButtonData = activity.completed_button_text_data || { key: activity.completed_button_text_key };
  const completedButtonText = t(completedButtonData.key, completedButtonData);
  const openActivityButtonData = activity.open_activity_button_text_data || { key: activity.open_activity_button_text_key };
  const openActivityButtonText = t(openActivityButtonData.key, openActivityButtonData);
  const currentProgressKey = activity.current_progress_key || 'children.activity_sessions.current_progress';

  // Account needs reauthentication (expired OAuth token). Take precedence over
  // the generic connection-error panel so the kid sees the specific guidance.
  if (needsAccountReauth) {
    const serviceLabel = activity.service_name || activity.activity_type;
    const handleRetryReauth = () => {
      if (isLoadingSnapshot || (activity.requires_initial_snapshot && Object.keys(currentProgress).length === 0)) {
        handleRetryLoading();
      } else {
        handleCheckCompletion();
      }
    };
    return (
      <div className="card-body p-5">
        <div
          className="alert alert-warning border-0 rounded-4"
          role="alert"
          data-testid="account-reauth-panel"
        >
          <div className="d-flex align-items-start mb-3">
            <i className="bi bi-plug-fill me-3 fs-3 text-warning" aria-hidden="true" />
            <div>
              <h3 className="fw-bold fs-5 mb-2">
                {t('children.activity_sessions.account_reauth.title', { service: serviceLabel })}
              </h3>
              <p className="mb-0">
                {t('children.activity_sessions.account_reauth.body', { service: serviceLabel })}
              </p>
            </div>
          </div>
          <div className="d-flex flex-wrap justify-content-center gap-3 mt-3">
            <button
              type="button"
              className="btn btn-success btn-lg rounded-3 px-4 py-2 fw-bold"
              onClick={handleRetryReauth}
              disabled={isCheckingCompletion || isRetrying}
              data-testid="account-reauth-retry"
            >
              {(isCheckingCompletion || isRetrying) && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
              )}
              {t('children.activity_sessions.account_reauth.retry_button')}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-lg rounded-3 px-4 py-2 fw-bold"
              onClick={onSkipActivity}
              data-testid="account-reauth-skip"
            >
              {t('children.activity_sessions.account_reauth.skip_button')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Invalid snapshot format - show error panel with skip option
  // This handles stale data from old snapshot format gracefully
  if (hasInvalidSnapshot) {
    return (
      <div className="card-body p-5">
          <div className="text-center">
            <div className="alert alert-warning mb-4" data-testid="stale-data-error">
              <h5 className="alert-heading fw-bold">
                {t('children.activity_sessions.stale_data_error.title')}
              </h5>
              <p className="mb-0">
                {t('children.activity_sessions.stale_data_error.message', { service: activity.service_name })}
              </p>
            </div>
            <div className="d-flex justify-content-center">
              <button
                type="button"
                className="btn btn-primary btn-lg"
                onClick={onSkipActivity}
                data-testid="skip-stale-activity-button"
              >
                {t('children.activity_sessions.stale_data_error.skip_button')}
              </button>
            </div>
          </div>
      </div>
    );
  }

  // Loading error state - show error panel with retry and skip options
  if (loadingError && !isLoadingSnapshot) {
    return (
      <div className="card-body p-5">
          <div className="text-center">
            <div className="alert alert-danger mb-4">
              <h5 className="alert-heading fw-bold">Connection Error</h5>
              <p className="mb-0">Could not connect to {activity.service_name}. {loadingError}</p>
            </div>
            <div className="d-flex justify-content-center gap-3">
              <button
                type="button"
                className="btn btn-primary btn-lg"
                onClick={handleRetryLoading}
                disabled={isRetrying}
              >
                {isRetrying ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Retrying...
                  </>
                ) : (
                  'Try Again'
                )}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-lg"
                onClick={onSkipActivity}
              >
                Skip this activity
              </button>
            </div>
          </div>
      </div>
    );
  }

  // Loading state while fetching initial snapshot
  if (isLoadingSnapshot) {
    return (
      <div className="card-body p-5">
          <div className="text-center">
            <div className="spinner-border text-primary mb-4" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <h2 className="fw-bold fs-3 mb-3">
              {t('children.activity_sessions.loading_panel.title')}
            </h2>
            <p className="text-secondary fs-5 mb-4">
              {t('children.activity_sessions.loading_panel.description', { service: activity.service_name })}
            </p>
            <div className="d-flex justify-content-center">
              <TimeBasedProgressBar
                averageSeconds={session.estimated_check_progress_duration || 10}
              />
            </div>
          </div>
      </div>
    );
  }

  // Which courses to render progress cards for. The backend strategy owns
  // this (activity.progress_course_ids) so single-course activities like
  // ArtWorkout stay in sync with the snapshot even when a legacy
  // external_config lists a stale course id. Falls back to snapshot keys
  // when the strategy returns nothing configured.
  const configuredCourses = activity.progress_course_ids || activity.external_config?.courses || [];
  let selectedCourses;

  if (activity.activity_type === 'code_combat_ai') {
    // CodeCombat AI aggregates every AI HackStack module. The parent UI
    // saves a placeholder `courses: ["all"]` (via auto_course), but the
    // real catalog of slugs lives in the snapshot the strategy returns.
    // Drive the cards from the snapshot keys, filtered to >0 so we only
    // show modules the kid actually started.
    selectedCourses = Object.keys(currentProgress).filter(
      slug => Number(currentProgress[slug]) > 0
    );
  } else {
    selectedCourses = configuredCourses.length > 0 ? configuredCourses : Object.keys(currentProgress);
  }

  return (
    <>
      <div className="card-body p-5">
          {/* Activity title and instructions */}
          <div className="text-center mb-5">
            <h1 className="fw-bold fs-2 mb-3">{journeyTitle}</h1>
            {activity.service_account_name && (
              <div className="d-flex justify-content-center mb-3">
                <span
                  className="badge bg-primary-subtle text-primary border border-primary-subtle rounded-pill px-4 py-2 fw-medium"
                  style={{ fontSize: '16px' }}
                >
                  {activity.service_account_name}
                </span>
              </div>
            )}
            <p
              className="fs-5 text-secondary"
              dangerouslySetInnerHTML={{ __html: instructionText }}
            />
          </div>

          {/* Action buttons */}
          <div className="d-flex justify-content-center gap-3 mb-5">
            <button
              type="button"
              className="btn btn-secondary btn-lg rounded-3 px-4 py-3 fs-6 fw-bold"
              onClick={handleOpenServiceClick}
              data-testid="open-service-button"
            >
              {openActivityButtonText}
            </button>
            {serviceOpenedAt && (
              <button
                type="button"
                className="btn btn-success btn-lg rounded-3 px-5 py-3 fs-6 fw-bold"
                onClick={handleCheckCompletion}
                disabled={isCheckingCompletion}
                data-testid="completion-button"
              >
                {isCheckingCompletion ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {t('children.activity_sessions.no_progress_dialog.checking')}
                  </>
                ) : (
                  completedButtonText
                )}
              </button>
            )}
          </div>

          {/* Current progress display */}
          {selectedCourses.length > 0 && (
            <div className="bg-light rounded-3 p-4" data-testid="current-progress-card">
              <h3 className="text-center text-secondary fw-semibold fs-6 mb-4">
                {t(currentProgressKey)}
              </h3>
              <div className="row g-3 justify-content-center">
                {selectedCourses.map(courseCode => {
                  // New simplified format: currentProgress[courseCode] is just the score (number)
                  const score = currentProgress[courseCode];
                  if (score === undefined) return null;

                  // Look up course name from mapping (skip for single-course activities)
                  const courseNameMapping = activity.course_id_to_name_mapping || {};
                  const courseName = courseNameMapping[courseCode] || courseCode;
                  // Only show course name labels for CodeCombat and Khan Academy
                  // Other activities have default/single courses where the activity name already implies the course
                  const showCourseNameLabel = activity.activity_type === 'codecombat' || activity.activity_type === 'code_combat_ai' || activity.activity_type === 'khan_academy';
                  const isDuolingo = activity.activity_type === 'duolingo';

                  return (
                    <div key={courseCode} className="col-md-4">
                      <div className="card border border-secondary-subtle rounded-3 h-100">
                        <div className="card-body p-3 text-center">
                          {/* Only show course name for CodeCombat/Khan Academy (multi-course activities) */}
                          {showCourseNameLabel && (
                            <h4 className="fw-semibold fs-6 mb-2">{courseName}</h4>
                          )}
                          <p className={`text-secondary mb-0 ${!showCourseNameLabel ? 'fs-4 fw-bold' : 'small'}`}>
                            {score} {isDuolingo ? (activity.activity_sub_type === 'abc' ? t('children.activity_sessions.score_unit_lessons') : (activity.activity_sub_type === 'elochess' ? '' : 'XP')) : ''}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
      </div>

      {/* Checking Progress Modal */}
      {isCheckingCompletion && (
        <div className="modal show d-block" id="completionProgressModal" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 rounded-4">
              <div className="modal-body text-center p-5">
                <div className="spinner-border text-primary mb-4" role="status" style={{ width: '3rem', height: '3rem' }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h5 className="fw-bold mb-3">
                  {t('children.activity_sessions.checking_progress.title')}
                </h5>
                <p className="text-secondary mb-4">
                  {t('children.activity_sessions.checking_progress.description', { service: activity.service_name })}
                </p>
                <div className="d-flex justify-content-center">
                  <TimeBasedProgressBar
                    averageSeconds={session.estimated_check_progress_duration || 10}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {errorMessage && (
        <div id="errorModal" className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h5 className="modal-title fw-bold text-danger">
                  {t('children.activity_sessions.error_modal.title')}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setErrorMessage(null)}
                ></button>
              </div>
              <div className="modal-body">
                <p className="text-secondary mb-0">{t('children.activity_sessions.error_modal.message', { service: activity.service_name })} {errorMessage}</p>
              </div>
              <div className="modal-footer border-0">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setErrorMessage(null);
                    handleCheckCompletion();
                  }}
                >
                  {t('children.activity_sessions.error_modal.try_again')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onSkipActivity}
                >
                  {t('children.activity_sessions.error_modal.skip_activity')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding Modal (for first-time users) */}
      <OnboardingModal
        show={showOnboardingModal}
        onHide={() => setShowOnboardingModal(false)}
        onComplete={handleOnboardingComplete}
        serviceName={activity.service_name}
        serviceUsername={activity.service_username}
        activityType={activity.activity_type}
        activitySubType={activity.activity_sub_type}
        instructionTextData={activity.instruction_text_data}
        courses={activity.external_config?.courses || []}
      />

      {/* Credentials Modal */}
      <CredentialsModal
        show={showCredentialsModal}
        onHide={() => setShowCredentialsModal(false)}
        serviceName={activity.service_name}
        serviceUsername={activity.service_username}
        servicePassword={activity.service_password}
        serviceProfileName={activity.service_profile_name}
        activityUrl={activity.activity_url}
        activityAppUrl={activity.activity_app_url}
        activityType={activity.activity_type}
        activitySubType={activity.activity_sub_type}
        courses={activity.external_config?.courses || []}
      />
    </>
  );
};
